import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import type { AppSettings } from '@/types'
import ModelListEditor from './SettingsDialog/ModelListEditor'
import SupplierWhitelistSection from './SettingsDialog/SupplierWhitelistSection'
import RecipientWhitelistSection from './SettingsDialog/RecipientWhitelistSection'
import { useT } from '@/i18n/useT'

interface SettingsDialogProps {
  open: boolean
  onClose: () => void
  onSave: (settings: AppSettings) => void
  initialSettings: AppSettings
}

export default function SettingsDialog({ open, onClose, onSave, initialSettings }: SettingsDialogProps) {
  const { t } = useT()
  // Invoice Analyser settings - using arrays now
  const defaultOcrModels = initialSettings.analyserOcrModels?.length ? initialSettings.analyserOcrModels : ['mistralai/mistral-ocr-2501']
  const defaultExtractionModels = initialSettings.analyserExtractionModels?.length ? initialSettings.analyserExtractionModels : ['google/gemini-3.1-flash-lite']

  const [ocrModels, setOcrModels] = useState<string[]>(defaultOcrModels)
  const [extractionModels, setExtractionModels] = useState<string[]>(defaultExtractionModels)
  const [selectedOcrModel, setSelectedOcrModel] = useState(defaultOcrModels[0])
  const [selectedExtractionModel, setSelectedExtractionModel] = useState(defaultExtractionModels[0])
  const [apiKey, setApiKey] = useState(initialSettings.analyserApiKey || '')
  const [showApiKey, setShowApiKey] = useState(false)

  useEffect(() => {
    const ocrM = initialSettings.analyserOcrModels?.length ? initialSettings.analyserOcrModels : ['mistralai/mistral-ocr-2501']
    const extM = initialSettings.analyserExtractionModels?.length ? initialSettings.analyserExtractionModels : ['google/gemini-3.1-flash-lite']
    setOcrModels(ocrM)
    setExtractionModels(extM)
    setSelectedOcrModel(ocrM[0])
    setSelectedExtractionModel(extM[0])
    setApiKey(initialSettings.analyserApiKey || '')
  }, [initialSettings, open])

  const handleSave = () => {
    onSave({
      analyserOcrModels: ocrModels,
      analyserExtractionModels: extractionModels,
      analyserApiKey: apiKey.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('Settings')}</DialogTitle>
      <DialogContent>
        {/* Invoice Analyser Settings */}
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('Invoice Analyser')}
          </Typography>

          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
            {t('Configure AI models for invoice document processing.')}
          </Typography>

          <ModelListEditor
            models={ocrModels}
            selectedModel={selectedOcrModel}
            onModelsChange={setOcrModels}
            onSelectedChange={setSelectedOcrModel}
            label={t('OCR model (for scans)')}
            helperText={t('Copy exact model IDs from openrouter.ai/models')}
          />

          <ModelListEditor
            models={extractionModels}
            selectedModel={selectedExtractionModel}
            onModelsChange={setExtractionModels}
            onSelectedChange={setSelectedExtractionModel}
            label={t('Extraction model')}
            helperText={t('Copy exact model IDs from openrouter.ai/models')}
          />

          <TextField
            fullWidth
            label={t('OpenRouter API key (optional)')}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            type={showApiKey ? 'text' : 'password'}
            size="small"
            placeholder="sk-or-..."
            helperText={t('For development only. In production the backend uses its own key.')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowApiKey(!showApiKey)}
                    edge="end"
                    size="small"
                  >
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider />

        {/* Supplier Whitelist */}
        <SupplierWhitelistSection open={open} />

        <Divider />

        {/* Recipient Whitelist */}
        <RecipientWhitelistSection open={open} />

        <Divider />

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            {t('Settings are stored locally in your browser.')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
